/**
 * The rule that names a dashboard panel inside a chat message.
 *
 * The composer writes these labels into the sentence, and the agent's turn envelope prints them back.
 * Both sides must produce the same label for the same panel, so the rule lives here and the frontend
 * imports it through the `@server/panel-mentions` alias. A second copy would drift, and the drift
 * would only show on a dashboard with two panels sharing a title — the one case the label exists for.
 */

export const MENTION_TRIGGER = '@';

/** A panel as it appears in a message: the stored title, plus the text that names exactly one panel. */
export interface PanelMention {
  id: string;
  title: string;
  label: string;
}

/** The fields the rule needs. `order` is what the dashboard grid sorts by. */
export interface MentionablePanel {
  id: string;
  title: string;
  order?: number;
}

/**
 * Panels in display order, each with a label that names exactly one of them.
 *
 * Titles are not unique — the spec validator enforces unique panel ids and nothing more — so a
 * repeated title takes a 1-based ordinal. The ordinal counts in **display order**, because that is
 * the order the reader sees on the page, and `@Revenue (1)` must be the first Revenue card on screen.
 *
 * Sorting happens here rather than at the call sites. A caller that passed the raw spec would
 * otherwise number the panels in storage order, and the server and the composer would disagree.
 */
export function buildPanelMentions(panels: MentionablePanel[]): PanelMention[] {
  const inDisplayOrder = [...panels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const countsByTitle = new Map<string, number>();
  for (const panel of inDisplayOrder) {
    countsByTitle.set(panel.title, (countsByTitle.get(panel.title) ?? 0) + 1);
  }

  const seenByTitle = new Map<string, number>();
  return inDisplayOrder.map((panel) => {
    const ordinal = (seenByTitle.get(panel.title) ?? 0) + 1;
    seenByTitle.set(panel.title, ordinal);
    const isDuplicated = (countsByTitle.get(panel.title) ?? 0) > 1;
    return {
      id: panel.id,
      title: panel.title,
      label: isDuplicated ? `${panel.title} (${String(ordinal)})` : panel.title,
    };
  });
}

/**
 * Makes a panel-supplied string safe to place in a block the model reads as instructions.
 *
 * A panel title is written by a workspace member, or by the dashboard agent through `save_dashboard`.
 * A title holding a newline and a closing tag would end the block and leave chosen text where
 * directives belong, so the characters that could do that are removed.
 */
export function sanitizeForPrompt(value: string): string {
  return value
    .replace(/[\r\n]+/g, ' ')
    .replace(/[<>]/g, '')
    .trim();
}
